<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-30">
      <div class="flex items-center justify-between px-4 py-3">
        <!-- Left side: Menu toggle and logo -->
        <div class="flex items-center space-x-4">
          <!-- Menu toggle button (mobile and desktop) -->
          <button 
            @click="toggleSidebar"
            class="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <!-- Logo -->
          <div class="flex items-center space-x-3">
            <img src="/static/logo.png" alt="Logo IBOLINK" class="w-8 h-8" />
            <span class="text-xl font-bold text-gray-900 tracking-wide">IBOLINK</span>
          </div>
        </div>

        <!-- Right side: User menu and notifications -->
        <div class="flex items-center space-x-4">
          <!-- Notifications -->
          <button class="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5zM10.5 3.75a6 6 0 0 1 6 6v3.75l-1.5 1.5H6l-1.5-1.5V9.75a6 6 0 0 1 6-6z" />
            </svg>
            <!-- Notification badge -->
            <span v-if="alerts.length > 0" class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {{ alerts.length > 9 ? '9+' : alerts.length }}
            </span>
          </button>

          <!-- User dropdown -->
          <div class="relative">
            <button 
              @click="toggleUserMenu"
              class="flex items-center space-x-2 p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span class="text-white font-medium text-sm">
                  {{ userInitials }}
                </span>
              </div>
              <span class="hidden md:block text-sm font-medium">{{ user ? user.name : 'Usuario' }}</span>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <!-- User dropdown menu -->
            <div v-if="showUserMenu" class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
              <a href="#" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Perfil</a>
              <a href="#" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Configuración</a>
              <hr class="my-1">
              <button 
                @click="handleLogout"
                class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Sidebar -->
    <aside 
      class="fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out"
      :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <!-- Sidebar header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Navegación</h2>
        <button 
          @click="toggleSidebar"
          class="p-1 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Navigation menu -->
      <nav class="mt-4 px-4">
        <div class="space-y-1">
          <router-link 
            to="/dashboard" 
            class="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="currentRoute === '/dashboard' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
            Dashboard
          </router-link>

          <router-link 
            to="/sensors" 
            class="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="currentRoute === '/sensors' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
            Sensores
          </router-link>

          <router-link 
            to="/devices" 
            class="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="currentRoute === '/devices' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Dispositivos
          </router-link>

          <router-link 
            to="/alerts" 
            class="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="currentRoute === '/alerts' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            Alertas
          </router-link>

          <router-link 
            to="/reports" 
            class="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="currentRoute === '/reports' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Reportes
          </router-link>

          <router-link 
            to="/settings" 
            class="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="currentRoute === '/settings' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Configuración
          </router-link>
        </div>
      </nav>
    </aside>

    <!-- Overlay for mobile -->
    <div 
      v-if="sidebarOpen && isMobile" 
      @click="toggleSidebar"
      class="fixed inset-0 bg-gray-600 bg-opacity-75 z-30"
    ></div>

    <!-- Main content -->
    <main 
      class="pt-16 transition-all duration-300 ease-in-out"
      :class="sidebarOpen ? 'ml-64' : 'ml-0'"
    >
      <div class="p-6">
        <!-- Content area for router-view -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <router-view />
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AppLayout',
  data() {
    return {
      sidebarOpen: window.innerWidth >= 1024, // Open by default on desktop
      showUserMenu: false,
      windowWidth: window.innerWidth
    }
  },
  
  mounted() {
    // Handle window resize to automatically show sidebar on desktop
    window.addEventListener('resize', this.handleResize)
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  computed: {
    ...mapGetters('auth', ['user']),
    ...mapGetters(['sensors', 'alerts']),
    currentRoute() {
      return this.$route.path
    },
    
    isMobile() {
      return this.windowWidth < 1024
    },
    userInitials() {
      if (!this.user || !this.user.name) return 'U'
      return this.user.name
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },
    pageTitle() {
      const path = this.$route.path
      const routeName = this.$route.name
      
      // Manejar rutas dinámicas
      if (path.startsWith('/devices/') && path !== '/devices') {
        return 'Detalle del Dispositivo'
      }
      
      const titles = {
        '/dashboard': 'Dashboard',
        '/sensors': 'Sensores',
        '/devices': 'Dispositivos',
        '/alerts': 'Alertas',
        '/reports': 'Reportes',
        '/settings': 'Configuración'
      }
      
      // Buscar por ruta exacta primero
      if (titles[path]) {
        return titles[path]
      }
      
      // Buscar por nombre de ruta
      const routeTitles = {
        'dashboard': 'Dashboard',
        'sensors': 'Sensores',
        'devices': 'Dispositivos',
        'device-detail': 'Detalle del Dispositivo',
        'alerts': 'Alertas',
        'reports': 'Reportes',
        'settings': 'Configuración'
      }
      
      if (routeName && routeTitles[routeName]) {
        return routeTitles[routeName]
      }
      
      return 'Página'
    },
    pageDescription() {
      const path = this.$route.path
      const routeName = this.$route.name
      
      // Manejar rutas dinámicas
      if (path.startsWith('/devices/') && path !== '/devices') {
        return 'Información detallada del dispositivo IoT'
      }
      
      const descriptions = {
        '/dashboard': 'Bienvenido a IBOLINK - Monitoreo inteligente en tiempo real',
        '/sensors': 'Gestión y monitoreo de sensores de temperatura y humedad',
        '/devices': 'Control y configuración de dispositivos IoT',
        '/alerts': 'Sistema de alertas y notificaciones',
        '/reports': 'Generación y visualización de reportes',
        '/settings': 'Configuración del sistema y preferencias'
      }
      
      // Buscar por ruta exacta primero
      if (descriptions[path]) {
        return descriptions[path]
      }
      
      // Buscar por nombre de ruta
      const routeDescriptions = {
        'dashboard': 'Bienvenido a IBOLINK - Monitoreo inteligente en tiempo real',
        'sensors': 'Gestión y monitoreo de sensores de temperatura y humedad',
        'devices': 'Control y configuración de dispositivos IoT',
        'device-detail': 'Información detallada del dispositivo IoT',
        'alerts': 'Sistema de alertas y notificaciones',
        'reports': 'Generación y visualización de reportes',
        'settings': 'Configuración del sistema y preferencias'
      }
      
      if (routeName && routeDescriptions[routeName]) {
        return routeDescriptions[routeName]
      }
      
      return 'Descripción de la página'
    }
  },
  methods: {
    ...mapActions('auth', ['logout']),
    
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },
    
    handleResize() {
      this.windowWidth = window.innerWidth
      // Auto-close sidebar on mobile, auto-open on desktop (but allow manual toggle)
      if (this.windowWidth < 1024 && this.sidebarOpen) {
        this.sidebarOpen = false // Close sidebar automatically on mobile
      }
    },
    
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu
    },
    
    async handleLogout() {
      try {
        await this.logout()
        this.$router.push('/login')
      } catch (error) {
        // Logout error - handled silently
      }
    }
  },
  mounted() {
    // Close user menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.relative')) {
        this.showUserMenu = false
      }
    })
  }
}
</script>

<style scoped>
/* Custom scrollbar for sidebar */
nav {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E0 #F7FAFC;
}

nav::-webkit-scrollbar {
  width: 6px;
}

nav::-webkit-scrollbar-track {
  background: #F7FAFC;
}

nav::-webkit-scrollbar-thumb {
  background-color: #CBD5E0;
  border-radius: 3px;
}

/* Smooth transitions */
.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Active link styles */
.router-link-active {
  background-color: #EBF8FF;
  color: #2563EB;
}

.router-link-active svg {
  color: #2563EB;
}
</style> 


// WEBPACK FOOTER //
// src/components/layout/AppLayout.vue